import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Requester profile block (avatar + name + Lv/KPS/completion chips) shared by the
 * leader-side join-request and conquer-request confirm modals. Rendered inside
 * ConfirmModal's `content`, so everything is styled dark-on-parchment with the
 * modalTheme.css tokens rather than the amber-on-dark notification palette.
 */
export interface AllianceRequesterSummary {
  requesterAvatarUrl?: string
  requesterNickName?: string
  requesterLevel: number
  requesterKps: number
  requesterCompletionPercent: number
}

export const AllianceRequestProfile: React.FC<{ req: AllianceRequesterSummary }> = ({ req }) => {
  const { t } = useTranslation()

  const chips = [
    { label: t('alliance.levelShort', { defaultValue: 'Lv {{n}}', n: req.requesterLevel }), key: 'lv' },
    { label: t('alliance.kpsStat', { defaultValue: 'KPS {{n}}', n: req.requesterKps.toLocaleString() }), key: 'kps' },
    { label: t('alliance.completionStat', { defaultValue: '{{n}}% complete', n: req.requesterCompletionPercent }), key: 'cmp' },
  ]

  return (
    <div className="flex flex-col items-center gap-2.5 text-center">
      <span
        className="flex h-[4.5rem] w-[4.5rem] items-center justify-center overflow-hidden rounded-full"
        style={{
          border: '2px solid rgba(145, 94, 33, 0.78)',
          background: 'rgba(255, 244, 211, 0.55)',
          boxShadow: '0 2px 6px rgba(80, 45, 3, 0.25), inset 0 1px 0 rgba(255, 249, 222, 0.8)',
        }}
      >
        {req.requesterAvatarUrl ? (
          <img src={req.requesterAvatarUrl} alt="" draggable={false} className="h-full w-full object-cover" />
        ) : (
          <span
            className="text-3xl font-bold"
            style={{ color: 'var(--cm-title)', fontFamily: 'var(--cm-display)' }}
          >
            {(req.requesterNickName || '?').trim().charAt(0).toUpperCase()}
          </span>
        )}
      </span>

      <div
        className="text-lg font-extrabold sm:text-xl"
        style={{
          color: 'var(--cm-title)',
          fontFamily: 'var(--cm-display)',
          textShadow: '0 1px 0 rgba(255, 224, 156, 0.6)',
        }}
      >
        {req.requesterNickName}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {chips.map((chip) => (
          <span
            key={chip.key}
            className="rounded-full px-3 py-1 text-xs font-bold sm:text-sm"
            style={{
              color: 'var(--cm-text)',
              fontFamily: 'var(--cm-display)',
              background: 'rgba(255, 244, 211, 0.5)',
              border: '1px solid rgba(145, 94, 33, 0.45)',
            }}
          >
            {chip.label}
          </span>
        ))}
      </div>
    </div>
  )
}

/** "(+N more pending)" note under the request message. */
export const AllianceRequestMoreNote: React.FC<{ remaining: number }> = ({ remaining }) => {
  const { t } = useTranslation()
  if (remaining <= 1) return null
  return (
    <span style={{ color: 'rgba(106, 60, 11, 0.8)' }}>
      {t('alliance.joinRequestMore', { defaultValue: '(+{{n}} more pending)', n: remaining - 1 })}
    </span>
  )
}
